import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import Slider from "./Slider";
import { fetchWithFallback } from "../config/api";

// =====================================
// ENGINE OPTIONS
// =====================================

const ENGINE_OPTIONS = [
    {
        value: "google",
        label: "Google TTS"
    },
    {
        value: "edge",
        label: "Edge TTS"
    },
    {
        value: "tiktok",
        label: "TikTok TTS"
    }
];

// =====================================
// TIKTOK DATA
// =====================================

const TIKTOK_DATA = [
  {
    group: "en-US",
    label: "English (US)",
    voices: [
      { value: "en_us_001", label: "Female - Original" },
      { value: "en_female_grandma", label: "Female - Granny" },
      { value: "en_us_002", label: "Female - Jessie" },
      { value: "en_male_deadpool", label: "Male - Deadpool" },
      { value: "en_us_006", label: "Male - Joey" },
      { value: "en_us_007", label: "Male - Professor" },
      { value: "en_us_009", label: "Male - Scientist" },
      { value: "en_us_010", label: "Male - Confidence" },
      { value: "en_male_m03_classical", label: "Male - Classic Electric" },
      { value: "en_male_jarvis", label: "Male - Alfred" },
      { value: "en_male_ashmagic", label: "Male - Ashmagic" },
      { value: "en_male_cupid", label: "Male - Cupid" },
      { value: "en_male_jomboy", label: "Male - Game On" },
      { value: "en_male_ukneighbor", label: "Male - Lord Cringe" },
      { value: "en_male_trevor", label: "Male - Marty" },
      { value: "en_male_ukbutler", label: "Male - Mr. Meticulous" },
      { value: "en_us_ghostface", label: "Male - Scream" },
      { value: "en_male_cody", label: "Male - Serious" },
      { value: "en_male_narration", label: "Male - Story Teller" },
      { value: "en_male_sing_funny_thanksgiving", label: "Male - Thanksgiving" },
      { value: "en_male_grinch", label: "Male - Trickster" },
      { value: "en_male_funny", label: "Male - Wacky" },
      { value: "en_male_wizard", label: "Male - Wizard" },
      { value: "en_female_ht_f08_halloween", label: "Halloween Song" },
      { value: "en_female_ht_f08_newyear", label: "NYE 2024 Song" }
    ]
  },

  {
    group: "en-GB",
    label: "English (UK)",
    voices: [
      { value: "en_uk_001", label: "Male 1" },
      { value: "en_uk_003", label: "Male 2" }
    ]
  },

  {
    group: "en-AU",
    label: "English (Australia)",
    voices: [
      { value: "en_au_001", label: "Female" },
      { value: "en_au_002", label: "Male" }
    ]
  },

  {
    group: "en-US-characters",
    label: "English (Characters)",
    voices: [
      { value: "en_us_ghostface", label: "Ghostface (Scream)" },
      { value: "en_us_chewbacca", label: "Chewbacca (Star Wars)" },
      { value: "en_us_c3po", label: "C3PO (Star Wars)" },
      { value: "en_us_stitch", label: "Stitch (Lilo & Stitch)" },
      { value: "en_us_stormtrooper", label: "Stormtrooper (Star Wars)" },
      { value: "en_us_rocket", label: "Rocket (Guardians)" },
      { value: "en_female_madam_leota", label: "Madame Leota" },
      { value: "en_male_ghosthost", label: "Ghost Host" },
      { value: "en_male_pirate", label: "Pirate" },
      { value: "en_male_deadpool", label: "Deadpool" },
      { value: "en_male_grinch", label: "The Grinch" },
      { value: "en_male_wizard", label: "Wizard" }
    ]
  },

  {
    group: "en-US-singing",
    label: "English (Singing)",
    voices: [
      { value: "en_female_ht_f08_halloween", label: "Halloween" },
      { value: "en_female_ht_f08_newyear", label: "NYE 2024" },
      { value: "en_female_f08_salut_damour", label: "Alto" },
      { value: "en_male_m03_lobby", label: "Tenor" },
      { value: "en_male_m03_sunshine_soon", label: "Sunshine Soon" },
      { value: "en_female_f08_warmy_breeze", label: "Warmy Breeze" },
      { value: "en_female_ht_f08_glorious", label: "Glorious" },
      { value: "en_male_sing_funny_it_goes_up", label: "It Goes Up" },
      { value: "en_male_m2_xhxs_m03_silly", label: "Chipmunk" },
      { value: "en_female_ht_f08_wonderful_world", label: "Dramatic" },
      { value: "en_male_sing_funny_thanksgiving", label: "Thanksgiving" },
      { value: "en_male_sing_deep_jingle", label: "Deep Jingle" }
    ]
  },

  {
    group: "fr-FR",
    label: "French",
    voices: [
      { value: "fr_002", label: "Male 1" },
      { value: "fr_001", label: "Male 2" }
    ]
  },

  {
    group: "de-DE",
    label: "German",
    voices: [
      { value: "de_001", label: "Female" },
      { value: "de_002", label: "Male" }
    ]
  },

  {
    group: "es-ES",
    label: "Spanish",
    voices: [
      { value: "es_002", label: "Male (Spain)" },
      { value: "es_mx_002", label: "Male (Mexico)" }
    ]
  },

  {
    group: "pt-BR",
    label: "Portuguese",
    voices: [
      { value: "br_001", label: "Female 1 (Brazil)" },
      { value: "br_003", label: "Female 2 (Brazil)" },
      { value: "br_004", label: "Female 3 (Brazil)" },
      { value: "br_005", label: "Male (Brazil)" },
      { value: "bp_female_ivete", label: "Ivete Sangalo" },
      { value: "pt_female_laizza", label: "Laizza" },
      { value: "pt_male_bueno", label: "Galvão Bueno" }
    ]
  },

  {
    group: "id-ID",
    label: "Indonesian",
    voices: [{ value: "id_001", label: "Female" }]
  },

  {
    group: "ja-JP",
    label: "Japanese",
    voices: [
      { value: "jp_001", label: "Female 1" },
      { value: "jp_002", label: "Female 2" },
      { value: "jp_005", label: "Female 3" },
      { value: "jp_006", label: "Male" },
      { value: "jp_female_fujicochan", label: "りーさ" },
      { value: "jp_female_hasegawariona", label: "世羅鈴" }
    ]
  },

  {
    group: "ko-KR",
    label: "Korean",
    voices: [
      { value: "kr_002", label: "Male 1" },
      { value: "kr_004", label: "Male 2" },
      { value: "kr_003", label: "Female" }
    ]
  },

  {
    group: "vi-VN",
    label: "Vietnamese",
    voices: [
      { value: "BV074_streaming", label: "Female" },
      { value: "BV075_streaming", label: "Male" }
    ]
  }
];

const TextToSpeech = () => {
    // =====================================
    // COMMON
    // =====================================

    const [text, setText] = useState("");
    const [engine, setEngine] = useState("google");

    const [audioURL, setAudioURL] = useState("");
    const [isConverting, setIsConverting] = useState(false);

    const fileInputRef = useRef(null);

    // =====================================
    // EDGE
    // =====================================

    const [edgeGroup, setEdgeGroup] = useState("en-US");
    const [edgeGroupOptions, setEdgeGroupOptions] =
        useState([]);

    const [edgeVoices, setEdgeVoices] =
        useState([]);

    const [edgeVoice, setEdgeVoice] =
        useState("");

    const [edgeSpeed, setEdgeSpeed] =
        useState(0);

    const [edgeVolume, setEdgeVolume] =
        useState(0);

    const [edgePitch, setEdgePitch] =
        useState(0);

    // =====================================
    // GOOGLE
    // =====================================

    const [googleSpeed, setGoogleSpeed] =
        useState(0);

    const [googlePitch, setGooglePitch] =
        useState(0);

    // =====================================
    // TIKTOK
    // =====================================

    const [tiktokGroup, setTiktokGroup] =
        useState("en-US");

    const [tiktokVoice, setTiktokVoice] =
        useState("");

    const tiktokVoices = useMemo(() => {
        return (
            TIKTOK_DATA.find(
                (item) =>
                    item.group === tiktokGroup
            )?.voices || []
        );
    }, [tiktokGroup]);

    useEffect(() => {
        if (tiktokVoices.length > 0) {
            setTiktokVoice(
                tiktokVoices[0].value
            );
        }
    }, [tiktokVoices]);

    // =====================================
    // AUDIO AUTO PLAY
    // =====================================

    useEffect(() => {
        if (!audioURL) return;

        const audio =
            document.getElementById("audio");

        if (audio) {
            audio.play().catch(console.error);
        }
    }, [audioURL]);

    // =====================================
    // LOAD EDGE GROUPS
    // =====================================

    useEffect(() => {
        const loadGroups = async () => {
            try {
                const res = await fetchWithFallback(
                    '?engine=edge&action=groups'
                );

                const result =
                    await res.json();

                const data =
                    typeof result.body ===
                    "string"
                        ? JSON.parse(
                              result.body
                          )
                        : result.body;

                setEdgeGroupOptions(
                    data.options || []
                );
            } catch (error) {
                console.error(error);
            }
        };

        loadGroups();
    }, []);

    // =====================================
    // LOAD EDGE VOICES
    // =====================================

    useEffect(() => {
        const loadVoices = async () => {
            try {
                const res = await fetchWithFallback(
                    `?engine=edge&action=voices-by-group&group=${encodeURIComponent(
                        edgeGroup
                    )}`
                );

                const result =
                    await res.json();

                const data =
                    typeof result.body ===
                    "string"
                        ? JSON.parse(
                              result.body
                          )
                        : result.body;

                const options =
                    data.options || [];

                setEdgeVoices(options);

                setEdgeVoice(
                    options[0]?.value || ""
                );
            } catch (error) {
                console.error(error);

                setEdgeVoices([]);
                setEdgeVoice("");
            }
        };

        loadVoices();
    }, [edgeGroup]);

    // =====================================
    // HELPERS
    // =====================================

    const isValidTTSChunk = (str) => {
        return /[\p{L}\p{N}]/u.test(
            str.trim()
        );
    };

    const mapGoogleSpeed = (
        value = 0
    ) => {
        const speed = 1 + value / 100;

        return Math.min(
            Math.max(
                Number(speed.toFixed(2)),
                0.5
            ),
            2
        );
    };

    const mapGooglePitch = (
        value = 0
    ) => {
        const min = 0.7;
        const max = 1.5;

        const pitch =
            1 + (value / 100) * (max - 1);

        return Number(
            Math.min(
                Math.max(pitch, min),
                max
            ).toFixed(2)
        );
    };

    const splitTextIntoChunks = (text, maxLength = 900) => {
        const chunks = [];
        let current = '';

        const sentences = text.match(/[^.!?]+[.!?]*/g) || [text];

        for (const sentence of sentences) {
            if (!sentence.trim()) continue;

            // nếu 1 câu quá dài → cắt thô
            if (sentence.length > maxLength) {
                if (current.trim()) {
                    chunks.push(current.trim());
                    current = '';
                }

                for (let i = 0; i < sentence.length; i += maxLength) {
                    chunks.push(sentence.slice(i, i + maxLength).trim());
                }
                continue;
            }

            if ((current + sentence).length > maxLength) {
                chunks.push(current.trim());
                current = sentence;
            } else {
                current += sentence;
            }
        }

        if (current.trim()) chunks.push(current.trim());

        return chunks;
    };

    const mergeAudioBlobs = (
        audioBlobs
    ) => {
        return new Blob(audioBlobs, {
            type: "audio/mpeg"
        });
    };

    const setFinalAudio = (blob) => {
        const url =
            URL.createObjectURL(blob);

        setAudioURL(url);
    };

    // =====================================
    // EDGE TTS
    // =====================================

    const callEdgeTTS = async ({
        text,
        voice,
        pitch = "+0Hz",
        rate = "0%",
        volume = "100%",
        format = "mp3"
    }) => {
        const res = await fetchWithFallback(
            '?engine=edge',
            {
                method: "POST",
                headers: {
                    "Content-Type":
                        "application/json"
                },
                body: JSON.stringify({
                    text,
                    voice,
                    pitch,
                    rate,
                    volume,
                    format
                })
            }
        );

        if (!res.ok) {
            throw new Error(
                "Edge TTS failed"
            );
        }

        return await res.blob();
    };

    const processEdgeTTS = async () => {
        const chunks =
            splitTextIntoChunks(
                text,
                900
            );

        const audioBlobs =
            await Promise.all(
                chunks.map((chunk) =>
                    callEdgeTTS({
                        text: chunk,
                        voice: edgeVoice,
                        rate: `${
                            edgeSpeed >= 0
                                ? "+"
                                : ""
                        }${edgeSpeed}%`,
                        volume: `${
                            edgeVolume >= 0
                                ? "+"
                                : ""
                        }${edgeVolume}%`,
                        pitch: `${
                            edgePitch >= 0
                                ? "+"
                                : ""
                        }${edgePitch}Hz`,
                        format: "mp3"
                    })
                )
            );

        const finalBlob =
            mergeAudioBlobs(audioBlobs);

        setFinalAudio(finalBlob);
    };

    // =====================================
    // GOOGLE TTS
    // =====================================

    const callGoogleTTS = async (
        text
    ) => {
        const res = await fetchWithFallback(
            '?engine=google',
            {
                method: "POST",
                headers: {
                    "Content-Type":
                        "application/json"
                },
                body: JSON.stringify({
                    text,
                    speed:
                        mapGoogleSpeed(
                            googleSpeed
                        ),
                    pitch:
                        mapGooglePitch(
                            googlePitch
                        )
                })
            }
        );

        if (!res.ok) {
            throw new Error(
                "Google TTS failed"
            );
        }

        return await res.blob();
    };

    const processGoogleTTS =
        async () => {
            const chunks =
                splitTextIntoChunks(
                    text,
                    900
                );

            const audioBlobs =
                await Promise.all(
                    chunks.map((chunk) =>
                        callGoogleTTS(
                            chunk
                        )
                    )
                );

            const finalBlob =
                mergeAudioBlobs(
                    audioBlobs
                );

            setFinalAudio(finalBlob);
        };

    // =====================================
    // TIKTOK TTS
    // =====================================

    const generateTikTokAudio =
        async (text, voice) => {
            const res = await fetchWithFallback(
                '?engine=tiktok',
                {
                    method: "POST",
                    headers: {
                        "Content-Type":
                            "application/json"
                    },
                    body: JSON.stringify({
                        text,
                        voice
                    })
                }
            );

            if (!res.ok) {
                throw new Error(
                    "TikTok TTS failed"
                );
            }

            return await res.blob();
        };

    async function processInBatches(items, batchSize, worker) {
        const results = [];

        for (let i = 0; i < items.length; i += batchSize) {
            const batch = items.slice(i, i + batchSize);

            const batchResults = await Promise.all(
                batch.map(worker)
            );

            results.push(...batchResults);
        }

        return results;
    }

    const processTikTokTTS = async () => {
        const MAX_TEXT_LENGTH = 80;

        let textParts = splitTextIntoChunks(text, MAX_TEXT_LENGTH);

        textParts = textParts
            .map((p) => p.trim())
            .filter(
                (p) =>
                    p.length > 0 &&
                    isValidTTSChunk(p)
            );

        // 10 request song song
        const audioBlobs = await processInBatches(
            textParts,
            30,
            (part) =>
                generateTikTokAudio(
                    part,
                    tiktokVoice
                )
        );

        const finalBlob = mergeAudioBlobs(audioBlobs);

        setFinalAudio(finalBlob);
    };

    // =====================================
    // CONVERT
    // =====================================

    const handleConvertAudio =
        async () => {
            if (!text.trim()) {
                alert(
                    "Please enter some text."
                );

                return;
            }

            try {
                setIsConverting(true);

                setAudioURL("");

                switch (engine) {
                    case "google":
                        await processGoogleTTS();
                        break;

                    case "tiktok":
                        await processTikTokTTS();
                        break;

                    case "edge":
                    default:
                        await processEdgeTTS();
                        break;
                }
            } catch (error) {
                console.error(error);

                alert(
                    error?.message ||
                        "Convert failed"
                );
            } finally {
                setIsConverting(false);
            }
        };

    // =====================================
    // FILE
    // =====================================

    const handleFileChange = (
        event
    ) => {
        const file =
            event.target.files?.[0];

        if (!file) return;

        const reader =
            new FileReader();

        reader.onload = (e) => {
            const content =
                e.target?.result;

            if (
                typeof content === "string"
            ) {
                setText(content);
            }
        };

        reader.readAsText(file);

        event.target.value = "";
    };

    // =====================================
    // ACTIONS
    // =====================================

    const handleClear = () => {
        setText("");
    };

    const handleCopy = async () => {
        if (!text) return;

        await navigator.clipboard.writeText(
            text
        );

        alert("Copied");
    };

    const handlePaste = async () => {
        try {
            const clipboardText =
                await navigator.clipboard.readText();

            setText(
                (prev) =>
                    prev + clipboardText
            );
        } catch (error) {
            alert("Clipboard denied");
        }
    };

    const handleDownloadText = () => {
        const blob = new Blob([text], {
            type: "text/plain;charset=utf-8"
        });

        const url =
            URL.createObjectURL(blob);

        const a =
            document.createElement("a");

        a.href = url;
        a.download = "text.txt";

        a.click();

        URL.revokeObjectURL(url);
    };

    const handleDownloadAudio = () => {
        if (!audioURL) {
            alert(
                "Please convert first"
            );

            return;
        }

        const a =
            document.createElement("a");

        a.href = audioURL;
        a.download = "audio.mp3";

        a.click();
    };

    return (
        <div className="card my-3">
            <div className="row">
                {/* LEFT */}
                <div className="col-lg-6">
                    <div className="card">
                        <div className="d-flex gap-2 p-2 flex-wrap">
                            <button
                                className="btn btn-outline-primary"
                                onClick={() =>
                                    fileInputRef.current.click()
                                }
                            >
                                Import
                            </button>

                            <button
                                className="btn btn-outline-primary"
                                onClick={
                                    handlePaste
                                }
                            >
                                Paste
                            </button>

                            <button
                                className="btn btn-outline-primary"
                                onClick={
                                    handleCopy
                                }
                            >
                                Copy
                            </button>

                            <button
                                className="btn btn-outline-secondary"
                                onClick={
                                    handleClear
                                }
                            >
                                Clear
                            </button>

                            <button
                                className="btn btn-outline-success"
                                onClick={
                                    handleDownloadText
                                }
                            >
                                Download
                            </button>

                            <input
                                type="file"
                                ref={
                                    fileInputRef
                                }
                                accept=".txt"
                                style={{
                                    display:
                                        "none"
                                }}
                                onChange={
                                    handleFileChange
                                }
                            />
                        </div>
                    </div>

                    <div
                        className="form-group"
                        style={{
                            position:
                                "relative"
                        }}
                    >
                        <textarea
                            className="form-control"
                            rows={10}
                            style={{
                                minHeight: 430,
                                paddingBottom: 30
                            }}
                            placeholder="Paste text..."
                            value={text}
                            onChange={(
                                e
                            ) =>
                                setText(
                                    e.target
                                        .value
                                )
                            }
                        />

                        <div
                            style={{
                                position:
                                    "absolute",
                                bottom: 8,
                                right: 20,
                                fontSize: 12,
                                color: "#666"
                            }}
                        >
                            {
                                text.length
                            }{" "}
                            characters
                        </div>
                    </div>
                </div>

                {/* RIGHT */}
                <div className="col-lg-6">
                    <div className="m-3">
                        {/* ENGINE */}
                        <div className="d-flex gap-3 flex-wrap my-3">
                            {ENGINE_OPTIONS.map(
                                (item) => (
                                    <div
                                        className="form-check"
                                        key={
                                            item.value
                                        }
                                    >
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="tts-engine"
                                            value={
                                                item.value
                                            }
                                            checked={
                                                engine ===
                                                item.value
                                            }
                                            onChange={(
                                                e
                                            ) =>
                                                setEngine(
                                                    e
                                                        .target
                                                        .value
                                                )
                                            }
                                        />

                                        <label className="form-check-label">
                                            {
                                                item.label
                                            }
                                        </label>
                                    </div>
                                )
                            )}
                        </div>

                        {/* EDGE */}
                        {engine === "edge" && (
                            <>
                                <Slider
                                    label="Speed"
                                    value={
                                        edgeSpeed
                                    }
                                    setValue={
                                        setEdgeSpeed
                                    }
                                    min={-100}
                                    max={100}
                                    unit="%"
                                />

                                <Slider
                                    label="Volume"
                                    value={
                                        edgeVolume
                                    }
                                    setValue={
                                        setEdgeVolume
                                    }
                                    min={-100}
                                    max={100}
                                    unit="%"
                                />

                                <Slider
                                    label="Pitch"
                                    value={
                                        edgePitch
                                    }
                                    setValue={
                                        setEdgePitch
                                    }
                                    min={-100}
                                    max={100}
                                    unit="Hz"
                                />

                                <div className="row">
                                    <div className="col-lg-6">
                                        <Select
                                            menuPortalTarget={
                                                document.body
                                            }
                                            styles={{
                                                menuPortal:
                                                    (
                                                        base
                                                    ) => ({
                                                        ...base,
                                                        zIndex: 9999
                                                    })
                                            }}
                                            value={edgeGroupOptions.find(
                                                (
                                                    option
                                                ) =>
                                                    option.value ===
                                                    edgeGroup
                                            )}
                                            onChange={(
                                                option
                                            ) =>
                                                setEdgeGroup(
                                                    option.value
                                                )
                                            }
                                            options={
                                                edgeGroupOptions
                                            }
                                            isSearchable
                                        />
                                    </div>

                                    <div className="col-lg-6">
                                        <Select
                                            menuPortalTarget={
                                                document.body
                                            }
                                            styles={{
                                                menuPortal:
                                                    (
                                                        base
                                                    ) => ({
                                                        ...base,
                                                        zIndex: 9999
                                                    })
                                            }}
                                            value={edgeVoices.find(
                                                (
                                                    option
                                                ) =>
                                                    option.value ===
                                                    edgeVoice
                                            )}
                                            onChange={(
                                                option
                                            ) =>
                                                setEdgeVoice(
                                                    option.value
                                                )
                                            }
                                            options={
                                                edgeVoices
                                            }
                                            isSearchable
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* GOOGLE */}
                        {engine === "google" && (
                            <>
                                <Slider
                                    label="Speed"
                                    value={
                                        googleSpeed
                                    }
                                    setValue={
                                        setGoogleSpeed
                                    }
                                    min={-100}
                                    max={100}
                                    unit="%"
                                />

                                <Slider
                                    label="Pitch"
                                    value={
                                        googlePitch
                                    }
                                    setValue={
                                        setGooglePitch
                                    }
                                    min={-100}
                                    max={100}
                                    unit="%"
                                />
                            </>
                        )}

                        {/* TIKTOK */}
                        {engine === "tiktok" && (
                            <div className="row">
                                <div className="col-lg-6">
                                    <select
                                        className="form-select"
                                        value={
                                            tiktokGroup
                                        }
                                        onChange={(
                                            e
                                        ) =>
                                            setTiktokGroup(
                                                e
                                                    .target
                                                    .value
                                            )
                                        }
                                    >
                                        {TIKTOK_DATA.map(
                                            (
                                                g
                                            ) => (
                                                <option
                                                    key={
                                                        g.group
                                                    }
                                                    value={
                                                        g.group
                                                    }
                                                >
                                                    {
                                                        g.label
                                                    }
                                                </option>
                                            )
                                        )}
                                    </select>
                                </div>

                                <div className="col-lg-6">
                                    <select
                                        className="form-select"
                                        value={
                                            tiktokVoice
                                        }
                                        onChange={(
                                            e
                                        ) =>
                                            setTiktokVoice(
                                                e
                                                    .target
                                                    .value
                                            )
                                        }
                                    >
                                        {tiktokVoices.map(
                                            (
                                                voice
                                            ) => (
                                                <option
                                                    key={
                                                        voice.value
                                                    }
                                                    value={
                                                        voice.value
                                                    }
                                                >
                                                    {
                                                        voice.label
                                                    }
                                                </option>
                                            )
                                        )}
                                    </select>
                                </div>
                            </div>
                        )}

                        {/* CONVERT */}
                        <div className="text-center my-3">
                            <button
                                className="btn btn-primary"
                                onClick={
                                    handleConvertAudio
                                }
                                disabled={
                                    isConverting
                                }
                            >
                                {isConverting
                                    ? "Converting..."
                                    : "Convert"}
                            </button>
                        </div>

                        {/* AUDIO */}
                        <div className="row align-items-center">
                            <div className="col-lg-9">
                                <audio
                                    id="audio"
                                    className="w-100"
                                    controls
                                    src={audioURL}
                                />
                            </div>

                            <div className="col-lg-3">
                                <button
                                    className="btn btn-outline-primary mt-2"
                                    onClick={
                                        handleDownloadAudio
                                    }
                                >
                                    Download
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TextToSpeech;